// hooks/useProfile.js
import { useState, useEffect, useCallback } from 'react';

export const useProfile = (supabase, currentUser) => {
  const [userProfile, setUserProfile] = useState(null);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  // Load user profile when currentUser changes
  useEffect(() => {
    if (currentUser) {
      loadUserProfile(currentUser.id);
    } else {
      setUserProfile(null);
      setShowProfileSetup(false);
    }
  }, [currentUser, supabase]); // Only re-run when user or supabase changes

  const loadUserProfile = useCallback(
    async userId => {
      if (!userId) return;

      setProfileLoading(true);
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            // No profile found - show setup
            if (process.env.NODE_ENV === 'development') {
              console.log('No profile found, showing setup');
            }
            setShowProfileSetup(false);
          } else {
            console.error('Error loading profile:', error);
          }
        } else {
          setUserProfile(data);
          setShowProfileSetup(false);
        }
      } catch (err) {
        console.error('Profile loading error:', err);
      } finally {
        setProfileLoading(false);
      }
    },
    [supabase]
  );

  // Helper functions for displaying user info
  const getDisplayName = userProfile => {
    if (!userProfile) return 'Anonymous';
    return userProfile.display_name || userProfile.username;
  };

  const getUsernameDisplay = userProfile => {
    if (!userProfile) return 'anonymous';
    return `@${userProfile.username}`;
  };

  // Handle profile creation from ProfileSetup component
  const handleProfileCreated = profile => {
    setUserProfile(profile);
    setShowProfileSetup(false);
  };

  return {
    // State
    userProfile,
    setUserProfile,
    showProfileSetup,
    setShowProfileSetup,
    profileLoading,

    // Functions
    loadUserProfile,
    getDisplayName,
    getUsernameDisplay,
    handleProfileCreated,
  };
};
